import type {
  EquipmentCategory,
  EquipmentInstance,
  EquipmentObjectType,
  EquipmentPoint,
  EquipmentTemplate,
} from '@/features/equipment/equipmentTypes';
import {
  categoryToProcessType,
  DEFAULT_PROCESS_WAIT_SEC,
} from '@/features/equipment/equipmentConstants';
import type { FacilityRegion, FloorPlanTransform } from '@/types';
import { createEntityIdAllocator } from '@/utils/entityIds';

type ObjectType = Extract<EquipmentObjectType, 'equipment' | 'stocker'>;

type Affine2D = {
  a: number;
  b: number;
  c: number;
  d: number;
  tx: number;
  ty: number;
};

type Bounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

type DxfEntity = {
  type?: unknown;
  block?: unknown;
  layer?: unknown;
  x?: unknown;
  y?: unknown;
  r?: unknown;
  scaleX?: unknown;
  scaleY?: unknown;
  rotation?: unknown;
  start?: unknown;
  end?: unknown;
  points?: unknown;
  vertices?: unknown;
  text?: unknown;
  entities?: unknown;
  category?: unknown;
  [key: number]: unknown;
};

type DxfBlock = {
  name?: unknown;
  x?: unknown;
  y?: unknown;
  entities?: unknown;
};

type DxfDocument = {
  entities?: unknown;
  blocks?: unknown;
};

/** Backend `category.category_confidence` levels, lowest → highest. */
export type CategoryConfidence = 'low' | 'medium' | 'high';

export type DxfEquipmentExtractionProfile = {
  stockerTokens: string[];
  stockerRegexes: string[];
  equipmentTokens: string[];
  mainEquipmentTokens: string[];
  excludedTokens: string[];
  accessoryTokens: string[];
  processTokens: Record<EquipmentCategory, string[]>;
  minRawFootprintSize: number;
  maxRawFootprintSize: number;
  /**
   * Ignore TEXT / MTEXT insertion points when measuring a block. Labels are
   * often placed well off the tool body and drag the footprint with them.
   */
  ignoreTextInFootprint: boolean;
  /**
   * Tukey fence width (in IQRs) for discarding stray geometry when measuring a
   * block. A block usually carries a few points far from the tool body — a
   * keep-out rectangle, an off-to-the-side accessory — that inflate the box
   * past the tool it belongs to and make neighbouring overlays collide.
   * `0` disables trimming and keeps the full block extent.
   */
  footprintOutlierIqrScale: number;
  /** Only trim when a block has at least this many sample points. */
  footprintTrimMinPoints: number;
  /**
   * Drop an overlay whose footprint sits (almost) inside another overlay's.
   * Removes nested accessories and duplicate INSERTs stacked on one pad.
   */
  dropContainedFootprints: boolean;
  /** Share of the inner footprint that must be covered to call it contained. */
  containmentRatio: number;
  /**
   * Prefer the backend `category` object (primary_category / work_cell) over the
   * name-token heuristic. The heuristic is still used as a fallback whenever the
   * backend category is missing, `OTHER`, or below the confidence threshold.
   */
  useBackendCategory: boolean;
  /** Backend `primary_category` values treated as stockers. */
  stockerPrimaryCategories: string[];
  /**
   * When false (default), backend-labelled EQUIPMENT that has no `work_cell` and
   * looks like an accessory (by name token) is dropped to reduce clutter. When
   * true, every backend EQUIPMENT insert is kept (accessories included).
   */
  includeAccessories: boolean;
  /** Drop backend classifications below this confidence (null = keep all). */
  minCategoryConfidence: CategoryConfidence | null;
  /** Maps a backend `work_cell` to the app's coarse process category enum. */
  workCellCategoryMap: Record<string, EquipmentCategory>;
};

export const DEFAULT_FAB_EQUIPMENT_PROFILE: DxfEquipmentExtractionProfile = {
  stockerTokens: ['stocker', 'pristk', 'foup'],
  stockerRegexes: ['(^|[^a-z0-9])wip([^a-z0-9]|$).*?(^|[^a-z0-9])rack([^a-z0-9]|$)', '(^|[^a-z0-9])rack([^a-z0-9]|$).*?(^|[^a-z0-9])wip([^a-z0-9]|$)'],
  equipmentTokens: ['i-equip-tool', 'equip-tool', 'equipment', 'eqp', 'tool'],
  mainEquipmentTokens: ['-mf', '_mf'],
  excludedTokens: [
    'anno',
    'text',
    'dim',
    'dimension',
    'tui',
    'support',
    'facilities',
    'facility',
    'hvac',
    'sink',
    'cart',
    'monitor',
    'printer',
    'screen',
    'chair',
    'waste',
    'trash',
    'step',
    'platform',
  ],
  accessoryTokens: ['interface', 'controller', 'console', 'cabinet', 'blower', 'pump', 'sensor'],
  processTokens: {
    Photolithography: ['lith', 'photo', 'phto'],
    etch: ['etch'],
    implant: ['implant', 'impt'],
    PVD: ['pvd', 'depo', 'deposition'],
    CVD: ['cvd'],
    diffusion: ['diffusion', 'diff', 'thfm'],
    CMP: ['cmp'],
    custom: ['auto'],
  },
  // Footprints are trimmed to the tool body now, so the floor has to sit well
  // below the old block-union sizes or real tools drop out of the overlay set.
  minRawFootprintSize: 40,
  maxRawFootprintSize: 320,
  ignoreTextInFootprint: true,
  footprintOutlierIqrScale: 1.5,
  footprintTrimMinPoints: 12,
  dropContainedFootprints: true,
  containmentRatio: 0.9,
  useBackendCategory: true,
  stockerPrimaryCategories: ['STOCKER', 'WIP_RACK'],
  includeAccessories: false,
  minCategoryConfidence: null,
  workCellCategoryMap: {
    ETCH: 'etch',
    CMP: 'CMP',
    IMPT: 'implant',
    DIFF: 'diffusion',
  },
};

const STYLE_BY_TYPE: Record<ObjectType, string> = {
  equipment: '#3b82f6',
  stocker: '#0d9488',
};

const identity: Affine2D = { a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const num = (value: unknown): number | null =>
  Number.isFinite(value) ? Number(value) : null;

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

const arrayOfRecords = <T>(value: unknown): T[] =>
  Array.isArray(value) ? value.filter(isRecord).map((item) => item as T) : [];

const mergeProfile = (
  override?: Partial<DxfEquipmentExtractionProfile>
): DxfEquipmentExtractionProfile => ({
  ...DEFAULT_FAB_EQUIPMENT_PROFILE,
  ...override,
  processTokens: {
    ...DEFAULT_FAB_EQUIPMENT_PROFILE.processTokens,
    ...(override?.processTokens ?? {}),
  },
  workCellCategoryMap: {
    ...DEFAULT_FAB_EQUIPMENT_PROFILE.workCellCategoryMap,
    ...(override?.workCellCategoryMap ?? {}),
  },
});

const normalize = (value: string): string => value.toLowerCase();

const tokenHit = (haystack: string, tokens: string[]): boolean =>
  tokens.some((token) => haystack.includes(normalize(token)));

const regexHit = (haystack: string, regexes: string[]): boolean =>
  regexes.some((pattern) => new RegExp(pattern, 'i').test(haystack));

const combineText = (...values: string[]): string => normalize(values.filter(Boolean).join(' '));

const bboxPolygon = (bounds: Bounds): EquipmentPoint[] => [
  { x: bounds.minX, y: bounds.minY },
  { x: bounds.maxX, y: bounds.minY },
  { x: bounds.maxX, y: bounds.maxY },
  { x: bounds.minX, y: bounds.maxY },
];

const rawToCanvas = (x: number, y: number, transform: FloorPlanTransform): EquipmentPoint => ({
  x: (x - transform.rawMinX) * transform.invScale,
  y: (-y - transform.rawMinY) * transform.invScale,
});

const rawBoundsToCanvasPolygon = (bounds: Bounds, transform: FloorPlanTransform): EquipmentPoint[] => {
  const corners = [
    rawToCanvas(bounds.minX, bounds.minY, transform),
    rawToCanvas(bounds.maxX, bounds.minY, transform),
    rawToCanvas(bounds.maxX, bounds.maxY, transform),
    rawToCanvas(bounds.minX, bounds.maxY, transform),
  ];
  return bboxPolygon(boundsFromPoints(corners));
};

const applyPoint = (t: Affine2D, x: number, y: number): EquipmentPoint => ({
  x: t.a * x + t.c * y + t.tx,
  y: t.b * x + t.d * y + t.ty,
});

const insertTransform = (insert: DxfEntity, block: DxfBlock | undefined): Affine2D => {
  const sx = num(insert.scaleX) ?? 1;
  const sy = num(insert.scaleY) ?? 1;
  const rotation = ((num(insert.rotation) ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const baseX = num(block?.x) ?? 0;
  const baseY = num(block?.y) ?? 0;
  const ix = num(insert.x) ?? 0;
  const iy = num(insert.y) ?? 0;

  return {
    a: cos * sx,
    b: sin * sx,
    c: -sin * sy,
    d: cos * sy,
    tx: ix - cos * sx * baseX - -sin * sy * baseY,
    ty: iy - sin * sx * baseX - cos * sy * baseY,
  };
};

const pointFromValue = (value: unknown): EquipmentPoint | null => {
  if (!isRecord(value)) return null;
  const x = num(value.x ?? value[0]);
  const y = num(value.y ?? value[1]);
  return x === null || y === null ? null : { x, y };
};

const boundsFromPoints = (points: EquipmentPoint[]): Bounds => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }
  return { minX, minY, maxX, maxY };
};

const boundsSize = (bounds: Bounds): { width: number; height: number } => ({
  width: bounds.maxX - bounds.minX,
  height: bounds.maxY - bounds.minY,
});

const boundsArea = (bounds: Bounds): number => {
  const { width, height } = boundsSize(bounds);
  return width > 0 && height > 0 ? width * height : 0;
};

const intersectionArea = (a: Bounds, b: Bounds): number => {
  const width = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
  const height = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
  return width > 0 && height > 0 ? width * height : 0;
};

const quantile = (sorted: number[], q: number): number =>
  sorted[Math.min(sorted.length - 1, Math.max(0, Math.round((sorted.length - 1) * q)))];

/**
 * Extent of one axis, ignoring far outliers via a Tukey fence. Points beyond
 * `scale × IQR` past the quartiles are strays — a keep-out rectangle, an
 * accessory drawn off to the side — and would otherwise stretch the footprint
 * across neighbouring tools. Real edge geometry sits inside the fence, so the
 * tool's own outline is kept intact rather than clipped by a fixed percentage.
 */
const fencedExtent = (values: number[], scale: number): { min: number; max: number } => {
  const sorted = [...values].sort((a, b) => a - b);
  const q1 = quantile(sorted, 0.25);
  const q3 = quantile(sorted, 0.75);
  const iqr = q3 - q1;
  if (iqr <= 0) return { min: sorted[0], max: sorted[sorted.length - 1] };

  const lowFence = q1 - scale * iqr;
  const highFence = q3 + scale * iqr;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const value of sorted) {
    if (value < lowFence || value > highFence) continue;
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return Number.isFinite(min) && Number.isFinite(max)
    ? { min, max }
    : { min: sorted[0], max: sorted[sorted.length - 1] };
};

/**
 * Bounds of the geometry that actually forms the tool body. Sample points (not
 * a running union) are what make this work: the outline contributes many
 * points and a lone stray only a handful, so the stray falls outside the fence.
 */
const trimmedBounds = (
  points: EquipmentPoint[],
  profile: DxfEquipmentExtractionProfile
): Bounds => {
  if (
    profile.footprintOutlierIqrScale <= 0 ||
    points.length < profile.footprintTrimMinPoints
  ) {
    return boundsFromPoints(points);
  }
  const x = fencedExtent(points.map((point) => point.x), profile.footprintOutlierIqrScale);
  const y = fencedExtent(points.map((point) => point.y), profile.footprintOutlierIqrScale);
  return { minX: x.min, minY: y.min, maxX: x.max, maxY: y.max };
};

const primitivePoints = (
  entity: DxfEntity,
  transform: Affine2D,
  profile: DxfEquipmentExtractionProfile
): EquipmentPoint[] => {
  const type = text(entity.type);
  const points: EquipmentPoint[] = [];

  if (type === 'LINE') {
    const start = pointFromValue(entity.start);
    const end = pointFromValue(entity.end);
    if (start) points.push(applyPoint(transform, start.x, start.y));
    if (end) points.push(applyPoint(transform, end.x, end.y));
  } else if (type === 'LWPOLYLINE' || type === 'POLYLINE') {
    const source = type === 'POLYLINE' ? entity.vertices : entity.points;
    if (Array.isArray(source)) {
      for (const rawPoint of source) {
        const point = pointFromValue(rawPoint);
        if (point) points.push(applyPoint(transform, point.x, point.y));
      }
    }
  } else if (type === 'CIRCLE' || type === 'ARC') {
    const cx = num(entity.x);
    const cy = num(entity.y);
    const r = num(entity.r);
    if (cx !== null && cy !== null && r !== null) {
      points.push(applyPoint(transform, cx - r, cy - r));
      points.push(applyPoint(transform, cx + r, cy + r));
    }
  } else if (type === 'TEXT' || type === 'MTEXT') {
    if (profile.ignoreTextInFootprint) return points;
    const point = pointFromValue(entity.text) ?? pointFromValue(entity);
    if (point) points.push(applyPoint(transform, point.x, point.y));
  }

  return points;
};

const transformedBounds = (bounds: Bounds, transform: Affine2D): Bounds =>
  boundsFromPoints([
    applyPoint(transform, bounds.minX, bounds.minY),
    applyPoint(transform, bounds.maxX, bounds.minY),
    applyPoint(transform, bounds.maxX, bounds.maxY),
    applyPoint(transform, bounds.minX, bounds.maxY),
  ]);

const blockLocalBounds = (
  blockName: string,
  blocksByName: Map<string, DxfBlock>,
  cache: Map<string, Bounds | null>,
  profile: DxfEquipmentExtractionProfile,
  stack = new Set<string>()
): Bounds | null => {
  if (cache.has(blockName)) return cache.get(blockName) ?? null;
  if (stack.has(blockName)) return null;

  const block = blocksByName.get(blockName);
  if (!block) return null;

  stack.add(blockName);
  const points: EquipmentPoint[] = [];
  for (const entity of arrayOfRecords<DxfEntity>(block.entities)) {
    if (text(entity.type) === 'INSERT') {
      const nestedName = text(entity.block);
      const nestedBlock = blocksByName.get(nestedName);
      const nestedBounds = nestedName
        ? blockLocalBounds(nestedName, blocksByName, cache, profile, stack)
        : null;
      if (nestedBounds) {
        const nested = insertTransform(entity, nestedBlock);
        points.push(
          applyPoint(nested, nestedBounds.minX, nestedBounds.minY),
          applyPoint(nested, nestedBounds.maxX, nestedBounds.minY),
          applyPoint(nested, nestedBounds.maxX, nestedBounds.maxY),
          applyPoint(nested, nestedBounds.minX, nestedBounds.maxY)
        );
      }
    } else {
      for (const point of primitivePoints(entity, identity, profile)) points.push(point);
    }
  }
  stack.delete(blockName);
  const bounds = points.length > 0 ? trimmedBounds(points, profile) : null;
  cache.set(blockName, bounds);
  return bounds;
};

/**
 * Remove overlays whose footprint is essentially inside another's. Largest
 * first, so an enclosing tool is settled before the parts drawn within it.
 */
const dropContainedFootprints = (
  instances: EquipmentInstance[],
  profile: DxfEquipmentExtractionProfile
): EquipmentInstance[] => {
  const boxes = instances.map((inst) => boundsFromPoints(inst.polygon));
  const areas = boxes.map(boundsArea);
  const byAreaDesc = instances.map((_, index) => index).sort((a, b) => areas[b] - areas[a]);
  const dropped = new Set<number>();

  for (let i = 0; i < byAreaDesc.length; i += 1) {
    const outer = byAreaDesc[i];
    if (dropped.has(outer)) continue;
    for (let j = i + 1; j < byAreaDesc.length; j += 1) {
      const inner = byAreaDesc[j];
      if (dropped.has(inner) || areas[inner] <= 0) continue;
      const covered = intersectionArea(boxes[outer], boxes[inner]) / areas[inner];
      if (covered >= profile.containmentRatio) dropped.add(inner);
    }
  }

  return dropped.size > 0 ? instances.filter((_, index) => !dropped.has(index)) : instances;
};

const rootEntities = (doc: DxfDocument, blocksByName: Map<string, DxfBlock>): DxfEntity[] => {
  const direct = arrayOfRecords<DxfEntity>(doc.entities);
  if (direct.length > 0) return direct;
  return arrayOfRecords<DxfEntity>(blocksByName.get('*MODEL_SPACE')?.entities);
};

const isUsableFootprint = (bounds: Bounds, profile: DxfEquipmentExtractionProfile): boolean => {
  const { width, height } = boundsSize(bounds);
  const longest = Math.max(width, height);
  const shortest = Math.min(width, height);
  return (
    Number.isFinite(width) &&
    Number.isFinite(height) &&
    shortest >= profile.minRawFootprintSize &&
    longest <= profile.maxRawFootprintSize
  );
};

const categoryFromText = (
  haystack: string,
  profile: DxfEquipmentExtractionProfile
): EquipmentCategory => {
  for (const [category, tokens] of Object.entries(profile.processTokens) as Array<
    [EquipmentCategory, string[]]
  >) {
    if (category !== 'custom' && tokenHit(haystack, tokens)) return category;
  }
  return 'custom';
};

const CONFIDENCE_RANK: Record<CategoryConfidence, number> = { low: 0, medium: 1, high: 2 };

/** Backend `category` object off an INSERT entity, when present. */
const insertCategory = (insert: DxfEntity): Record<string, unknown> | null =>
  isRecord(insert.category) ? insert.category : null;

/** Uppercased backend `primary_category`, or null. */
const primaryCategoryFromInsert = (insert: DxfEntity): string | null => {
  const value = insertCategory(insert)?.primary_category;
  return typeof value === 'string' && value.length > 0 ? value.toUpperCase() : null;
};

/** Backend `category_confidence`, or null. */
const confidenceFromInsert = (insert: DxfEntity): CategoryConfidence | null => {
  const value = insertCategory(insert)?.category_confidence;
  return value === 'low' || value === 'medium' || value === 'high' ? value : null;
};

/** Whether a backend classification clears the profile confidence threshold. */
const meetsConfidence = (
  insert: DxfEntity,
  profile: DxfEquipmentExtractionProfile
): boolean => {
  const min = profile.minCategoryConfidence;
  if (!min) return true;
  const c = confidenceFromInsert(insert);
  return c !== null && CONFIDENCE_RANK[c] >= CONFIDENCE_RANK[min];
};

/** Map a backend `work_cell` onto the app's coarse process-category enum. */
const categoryFromWorkCell = (
  workCell: string | null,
  profile: DxfEquipmentExtractionProfile
): EquipmentCategory => {
  if (!workCell) return 'custom';
  return profile.workCellCategoryMap[workCell.toUpperCase()] ?? 'custom';
};

const regionText = (region: FacilityRegion): string =>
  combineText(region.name, region.layer, ...region.equipment);

const regionArea = (region: FacilityRegion): number => region.width * region.height;

const containsRawPoint = (region: FacilityRegion, x: number, y: number, transform: FloorPlanTransform): boolean => {
  const point = rawToCanvas(x, y, transform);
  return (
    point.x >= region.x &&
    point.x <= region.x + region.width &&
    point.y >= region.y &&
    point.y <= region.y + region.height
  );
};

const isProcessRegion = (region: FacilityRegion, profile: DxfEquipmentExtractionProfile): boolean => {
  const haystack = regionText(region);
  if (region.type === 'process_bay') return !tokenHit(haystack, ['anno', 'text']);
  if (region.type !== 'equipment_area') return false;
  return categoryFromText(haystack, profile) !== 'custom';
};

const containingProcessRegion = (
  regions: FacilityRegion[] | undefined,
  x: number,
  y: number,
  transform: FloorPlanTransform,
  profile: DxfEquipmentExtractionProfile
): FacilityRegion | null => {
  if (!regions || regions.length === 0) return null;
  const matches = regions.filter(
    (region) => isProcessRegion(region, profile) && containsRawPoint(region, x, y, transform)
  );
  matches.sort((a, b) => regionArea(a) - regionArea(b));
  return matches[0] ?? null;
};

type Classification = { objectType: ObjectType; category: EquipmentCategory; text: string };

/** Legacy name-token heuristic (used as a fallback when the backend can't decide). */
const heuristicClassify = (
  haystack: string,
  profile: DxfEquipmentExtractionProfile
): Classification | null => {
  const isStocker =
    tokenHit(haystack, profile.stockerTokens) || regexHit(haystack, profile.stockerRegexes);
  if (isStocker) return { objectType: 'stocker', category: 'custom', text: haystack };

  if (!tokenHit(haystack, profile.equipmentTokens)) return null;
  if (tokenHit(haystack, profile.excludedTokens)) return null;

  const isMainTool = tokenHit(haystack, profile.mainEquipmentTokens);
  const isAccessory = tokenHit(haystack, profile.accessoryTokens);
  if (!isMainTool && isAccessory) return null;

  return {
    objectType: 'equipment',
    category: categoryFromText(haystack, profile),
    text: haystack,
  };
};

const classifyInsert = (
  insert: DxfEntity,
  profile: DxfEquipmentExtractionProfile
): Classification | null => {
  const blockName = text(insert.block);
  const layerName = text(insert.layer);
  const haystack = combineText(blockName, layerName);

  // ── Backend-first: trust the ingestion pipeline's category when available ──
  if (profile.useBackendCategory) {
    const primary = primaryCategoryFromInsert(insert);
    // Only defer to the backend when it is confident enough; otherwise fall
    // through to the heuristic below.
    if (primary && meetsConfidence(insert, profile)) {
      const workCell = workCellFromInsert(insert);
      const category = categoryFromWorkCell(workCell, profile);

      const isStocker =
        profile.stockerPrimaryCategories.includes(primary) ||
        workCell?.toUpperCase() === 'AUTO';
      if (isStocker) return { objectType: 'stocker', category, text: haystack };

      if (primary === 'EQUIPMENT') {
        // Tools with a work_cell are real process equipment — always keep them.
        // For EQUIPMENT that the backend left without a work_cell, apply the
        // de-clutter name filters (unless accessories are explicitly requested):
        // the backend sometimes tags tool UIs, monitors and accessories as
        // EQUIPMENT, which would otherwise flood the overlay.
        if (!profile.includeAccessories && !workCell) {
          if (tokenHit(haystack, profile.excludedTokens)) return null;
          const isMainTool = tokenHit(haystack, profile.mainEquipmentTokens);
          const isAccessory = tokenHit(haystack, profile.accessoryTokens);
          if (!isMainTool && isAccessory) return null;
        }
        return {
          objectType: 'equipment',
          category: category !== 'custom' ? category : categoryFromText(haystack, profile),
          text: haystack,
        };
      }

      // Any other explicit backend category (FURNITURE, WALL, ANNOTATION, …) is
      // definitively not equipment. `OTHER` is inconclusive → use the heuristic.
      if (primary !== 'OTHER') return null;
    }
  }

  return heuristicClassify(haystack, profile);
};

const equipmentNameFromBlock = (blockName: string, fallback: string): string => {
  const cleaned = blockName.split('$').pop() || blockName;
  return cleaned || fallback;
};

/**
 * Reads the backend `category.work_cell` value off an INSERT entity.
 * Returns the trimmed work_cell string, or null when absent/empty/null.
 */
const workCellFromInsert = (insert: DxfEntity): string | null => {
  const category = insert.category;
  if (!isRecord(category)) return null;
  const raw = category.work_cell;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Maps a backend `work_cell` value to the process an equipment block belongs to.
 *   - null / missing → null (unknown)
 *   - "AUTO"        → "stocker" (AUTO indicates a stocker, not a process)
 *   - anything else → the work_cell value verbatim (e.g. "ETCH", "CMP", "METR")
 */
const processFromWorkCell = (workCell: string | null): string | null => {
  if (!workCell) return null;
  return workCell.toUpperCase() === 'AUTO' ? 'stocker' : workCell;
};

export function extractEquipmentFromDxfJson(
  doc: unknown,
  transform: FloorPlanTransform,
  facilityRegions?: FacilityRegion[],
  profileOverride?: Partial<DxfEquipmentExtractionProfile>,
  /**
   * Ids already in use (hand-drawn equipment, layout elements). Overlays are
   * re-generated while the editor holds other entities, so allocation has to
   * skip their ids instead of restarting from 1.
   */
  reservedIds: Iterable<string> = []
): EquipmentInstance[] {
  const parsedDoc = isRecord(doc) ? (doc as DxfDocument) : {};
  const blocks = arrayOfRecords<DxfBlock>(parsedDoc.blocks);
  const blocksByName = new Map<string, DxfBlock>();
  for (const block of blocks) {
    const name = text(block.name);
    if (name) blocksByName.set(name, block);
  }

  const profile = mergeProfile(profileOverride);
  const boundsCache = new Map<string, Bounds | null>();
  const now = new Date().toISOString();
  const instances: EquipmentInstance[] = [];

  for (const insert of rootEntities(parsedDoc, blocksByName)) {
    if (text(insert.type) !== 'INSERT') continue;

    const classification = classifyInsert(insert, profile);
    if (!classification) continue;
    // Temporarily focus generated overlays on process equipment only. Stocker
    // extraction stays classified above so it can be re-enabled later without
    // changing the backend/category rules.
    if (classification.objectType === 'stocker') continue;

    const blockName = text(insert.block);
    const block = blocksByName.get(blockName);
    const localBounds = blockName
      ? blockLocalBounds(blockName, blocksByName, boundsCache, profile)
      : null;
    if (!localBounds) continue;

    const rawBounds = transformedBounds(localBounds, insertTransform(insert, block));
    if (!isUsableFootprint(rawBounds, profile)) continue;

    const insertX = num(insert.x);
    const insertY = num(insert.y);
    const region =
      insertX !== null && insertY !== null
        ? containingProcessRegion(facilityRegions, insertX, insertY, transform, profile)
        : null;
    // Prefer the (backend-derived) classification category; fall back to the
    // facility region only when the classification is inconclusive ('custom').
    const regionCategory = region ? categoryFromText(regionText(region), profile) : 'custom';
    const category =
      classification.category !== 'custom' ? classification.category : regionCategory;
    const objectType = classification.objectType;
    const color = STYLE_BY_TYPE[objectType];
    const workCell = workCellFromInsert(insert);
    const process = processFromWorkCell(workCell);
    const reviewKey = JSON.stringify([
      blockName,
      text(insert.layer),
      insertX,
      insertY,
      num(insert.rotation) ?? 0,
      num(insert.scaleX) ?? 1,
      num(insert.scaleY) ?? 1,
    ]);
    const name = equipmentNameFromBlock(
      blockName,
      `Equipment ${instances.length + 1}`
    );

    instances.push({
      // Assigned after filtering so dropped candidates leave no gaps.
      id: '',
      templateId: null,
      name,
      objectType,
      category,
      equipmentType: categoryToProcessType(category),
      description: `Extracted from CAD block: ${blockName}`,
      tags: [
        'backend-json-import',
        'insert-extraction',
        ...(region ? [region.type] : []),
        ...(category !== 'custom' ? [category] : []),
      ],
      notes: '',
      polygon: rawBoundsToCanvasPolygon(rawBounds, transform),
      rotation: 0,
      processWaitSec: DEFAULT_PROCESS_WAIT_SEC,
      reviewStatus: 'potential',
      overrides: {
        style: {
          color,
          fillOpacity: 0.14,
          icon: '',
        },
        properties: {
          blockName,
          layer: text(insert.layer),
          processRegion: region?.layer ?? null,
          process,
        },
      },
      metadata: {
        source: 'backend-json-insert',
        block: blockName,
        layer: text(insert.layer),
        workCell,
        process,
        reviewKey,
        rawBounds,
        insert: {
          x: insertX,
          y: insertY,
          rotation: num(insert.rotation) ?? 0,
          scaleX: num(insert.scaleX) ?? 1,
          scaleY: num(insert.scaleY) ?? 1,
        },
        processRegionId: region?.id,
        processRegionLayer: region?.layer,
        processRegionType: region?.type,
      },
      createdAt: now,
      updatedAt: now,
    });
  }

  const kept = profile.dropContainedFootprints
    ? dropContainedFootprints(instances, profile)
    : instances;

  const alloc = createEntityIdAllocator(reservedIds);
  for (const inst of kept) inst.id = alloc.nextForEquipment(inst.objectType);
  return kept;
}

/**
 * Groups raw extracted equipment instances into reusable templates and maps them
 * to populate both the Equipment Library (templates) and the canvas (instances).
 */
export function processExtractedEquipment(extracted: EquipmentInstance[]): {
  templates: Record<string, EquipmentTemplate>;
  instances: Record<string, EquipmentInstance>;
  templateOrder: string[];
  instanceOrder: string[];
} {
  const templates: Record<string, EquipmentTemplate> = {};
  const templateOrder: string[] = [];
  const instances: Record<string, EquipmentInstance> = {};
  const instanceOrder: string[] = [];

  const CATEGORY_COLORS = {
    Photolithography: '#d946ef',
    etch: '#ef4444',
    implant: '#f59e0b',
    PVD: '#3b82f6',
    CVD: '#06b6d4',
    diffusion: '#f97316',
    CMP: '#10b981',
    custom: '#6366f1',
  };

  const CATEGORY_ICONS = {
    Photolithography: '🔬',
    etch: '🧪',
    implant: '⚛️',
    PVD: '💧',
    CVD: '🌫️',
    diffusion: '🔥',
    CMP: '🪞',
    custom: '⚙️',
  };

  for (const inst of extracted) {
    const blockName = (inst.overrides.properties?.blockName as string) || inst.name || 'Unknown Tool';
    const templateId = `tpl-${blockName}`;

    if (!templates[templateId]) {
      const color = CATEGORY_COLORS[inst.category as keyof typeof CATEGORY_COLORS] || CATEGORY_COLORS.custom;
      const icon = CATEGORY_ICONS[inst.category as keyof typeof CATEGORY_ICONS] || CATEGORY_ICONS.custom;
      const isoNow = new Date().toISOString();

      templates[templateId] = {
        id: templateId,
        name: blockName,
        objectType: inst.objectType,
        category: inst.category,
        icon: icon,
        defaultStyle: {
          color,
          fillOpacity: 0.18,
          icon,
        },
        ports: [],
        defaultProperties: {
          blockName,
        },
        metadata: { source: 'auto-extraction' },
        createdAt: isoNow,
        updatedAt: isoNow,
      };
      templateOrder.push(templateId);
    }

    const updatedInstance: EquipmentInstance = {
      ...inst,
      templateId,
    };

    instances[inst.id] = updatedInstance;
    instanceOrder.push(inst.id);
  }

  return {
    templates,
    instances,
    templateOrder,
    instanceOrder,
  };
}
